
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

export interface IDailyBarChartPoint {
  date: string;
  count: number;
}

const X_LABEL_TARGET = 5;
const DEFAULT_SERIES_COLOR = '#2a78d6';

/**
 * One series of daily counts as columns: thin rounded bars, hairline grid with round ticks,
 * the peak labelled, a tooltip per bar (hover and keyboard focus) and a table view twin.
 */
@Component({
  name: 'DailyBarChart',
})
export default class DailyBarChart extends Vue {
  @Prop({ required: true })
  protected points!: IDailyBarChartPoint[];

  // Plural noun of what is counted ("users"), used in the caption, tooltip and table header.
  @Prop({ required: true })
  protected unitLabel!: string;

  @Prop({ default: '' })
  protected totalCaption!: string;

  @Prop({ default: '' })
  protected emptyLabel!: string;

  // Shown in the tooltip, e.g. what clicking a bar does.
  @Prop({ default: '' })
  protected hint!: string;

  // The selected day: the other bars are dimmed.
  @Prop({ default: null })
  protected activeDate!: string | null;

  @Prop({ default: DEFAULT_SERIES_COLOR })
  protected color!: string;

  protected hovered: number | null = null;
  protected showTable: boolean = false;

  get total(): number {
    return this.points.reduce((sum, point) => sum + point.count, 0);
  }

  get reversedPoints(): IDailyBarChartPoint[] {
    return this.points.slice().reverse();
  }

  get hoveredPoint(): IDailyBarChartPoint | null {
    return this.hovered === null ? null : this.points[this.hovered] || null;
  }

  get peakIndex(): number {
    let peak = -1;
    this.points.forEach((point, index) => {
      if (point.count > 0 && (peak === -1 || point.count > this.points[peak].count)) {
        peak = index;
      }
    });

    return peak;
  }

  // Round step (1, 2, 5 × 10^n) giving about three gridlines above the baseline.
  get step(): number {
    const max = Math.max(1, ...this.points.map(point => point.count));
    const raw = max / 3;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const nice = [1, 2, 5, 10].find(factor => factor * magnitude >= raw) || 10;

    return Math.max(1, nice * magnitude);
  }

  get scaleMax(): number {
    const max = Math.max(1, ...this.points.map(point => point.count));

    return Math.ceil(max / this.step) * this.step;
  }

  get ticks(): number[] {
    const ticks: number[] = [];
    for (let tick = 0; tick <= this.scaleMax; tick += this.step) {
      ticks.push(tick);
    }

    return ticks;
  }

  // Evenly spaced dates, always including the last day; a label too close to the last one is dropped.
  get xLabelIndexes(): number[] {
    const last = this.points.length - 1;
    if (last < 0) {
      return [];
    }
    const every = Math.max(1, Math.ceil(this.points.length / X_LABEL_TARGET));
    const indexes: number[] = [];
    for (let index = 0; index < last; index += every) {
      if (last - index >= every / 2 || index === 0) {
        indexes.push(index);
      }
    }
    indexes.push(last);

    return indexes;
  }

  barPercent(count: number): number {
    return (count / this.scaleMax) * 100;
  }

  tickPercent(tick: number): number {
    return (tick / this.scaleMax) * 100;
  }

  slotCenterPercent(index: number): number {
    return ((index + 0.5) / this.points.length) * 100;
  }

  formatNumber(value: number): string {
    return value.toLocaleString(this.$i18n.locale);
  }

  shortDate(date: string): string {
    return this.toDate(date).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' });
  }

  longDate(date: string): string {
    return this.toDate(date).toLocaleDateString(this.$i18n.locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }

  // 'YYYY-MM-DD' as a local calendar day (Date('YYYY-MM-DD') would be UTC midnight).
  private toDate(date: string): Date {
    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day);
  }
}
